import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionRequiredAuthError,
    InteractionStatus,
    InteractionType,
    IPublicClientApplication,
    PopupRequest,
    PublicClientApplication,
    RedirectRequest,
    SsoSilentRequest
} from '@azure/msal-browser';
import {filter, map, takeUntil} from 'rxjs/operators';
import {b2cPolicies, loginRequest, loginRestrictions, msalConfig, scopeConfig} from './auth-config';
import {IdTokenClaims, PromptValue} from '@azure/msal-common';
import {AccessRights} from './access-rights';

export interface TokenUserData {
    familyName?: string;
    mail?: string;
    giveName?: string;
    cloudId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private accessRightsSubject: BehaviorSubject<any> = new BehaviorSubject(null);
    public accessRights$: Observable<AccessRights> = this.accessRightsSubject.asObservable();

    public loggedIn$: Observable<boolean> = this.accessRights$.pipe(map(x => {
        if (this.authService.instance.getActiveAccount()) {
            return true;
        }
        return false;
    }));

    public accessGranted$: Observable<boolean> = this.accessRights$.pipe(map(x => {
        if (x !== undefined && x !== null) {
            if (this.authService.instance.getActiveAccount()) {
                if (loginRestrictions.length > 0)
                    return loginRestrictions.some(ur => x.isUserRightAvailable(ur));
                return true;
            }
        }
        return false;
    }));


    private initialziedSubject: BehaviorSubject<any> = new BehaviorSubject(false);
    public initialzied$: Observable<TokenUserData> = this.initialziedSubject.asObservable();

    private userSubject: BehaviorSubject<any> = new BehaviorSubject(null);
    public user$: Observable<TokenUserData> = this.userSubject.asObservable();

    private basicUserSubject: BehaviorSubject<any> = new BehaviorSubject(null);
    public basicUser$: Observable<TokenUserData> = this.basicUserSubject.asObservable();

    isIframe = false;
    loginDisplay = false;

    private readonly _destroying$ = new Subject<void>();

    constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
                private authService: MsalService,
                private msalBroadcastService: MsalBroadcastService) {
    }

    async init() {
        await this.initMsal();
        await this.checkAndSetActiveAccount();
    }

    public async getAccessToken(): Promise<string | void> {
        const request = {
            scopes: [scopeConfig],
        };
        await this.authService.instance.initialize();
        return this.authService.instance
            .acquireTokenSilent(request).then(tokenResponse => {
                return tokenResponse.accessToken;
            }).catch(error => {
                if (error instanceof InteractionRequiredAuthError) {
                    // fallback to interaction when silent call fails
                    return this.authService.instance.acquireTokenRedirect(request);
                }
                throw error;
            });
    }

    private async initMsal() {
        this.authService.initialize()
            .subscribe(x => {
                //All the code that was previously in ngOnInit
                this.isIframe = window !== window.parent && !window.opener;

                this.msalBroadcastService.inProgress$
                    .pipe(
                        filter(
                            (status: InteractionStatus) => status === InteractionStatus.None
                        ),
                        takeUntil(this._destroying$)
                    )
                    .subscribe(async () => {
                        this.setLoginDisplay();
                        await this.checkAndSetActiveAccount();
                    });

            });
        //await this.authService.instance.initialize();

        this.isIframe = window !== window.parent && !window.opener;

        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

        /**
         * You can subscribe to MSAL events as shown below. For more info,
         * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
         */
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginDisplay();
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
                    || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                    || msg.eventType === EventType.SSO_SILENT_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe(async (result: EventMessage) => {

                let payload = result.payload as AuthenticationResult;
                let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

                if (idtoken.acr === b2cPolicies.names.signUpSignIn || idtoken.tfp === b2cPolicies.names.signUpSignIn) {
                    this.authService.instance.setActiveAccount(payload.account);
                }

                /**
                 * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
                 * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
                 * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                 */
                if (idtoken.acr === b2cPolicies.names.editProfile || idtoken.tfp === b2cPolicies.names.editProfile) {

                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = this.authService.instance.getAllAccounts()
                        .find((account: AccountInfo) =>
                            account.idTokenClaims?.oid === idtoken.oid
                            && account.idTokenClaims?.sub === idtoken.sub
                            && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === b2cPolicies.names.signUpSignIn
                                || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === b2cPolicies.names.signUpSignIn)
                        );

                    let signUpSignInFlowRequest: SsoSilentRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount
                    };

                    // silently login again with the signUpSignIn policy
                    this.authService.ssoSilent(signUpSignInFlowRequest);
                }

                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow (see above ln. 74-92).
                 */
                if (idtoken.acr === b2cPolicies.names.resetPassword || idtoken.tfp === b2cPolicies.names.resetPassword) {
                    let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        prompt: PromptValue.LOGIN, // force user to reauthenticate with their new password
                        scopes: []
                    };

                    await this.login(signUpSignInFlowRequest);
                }

                return result;
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
                takeUntil(this._destroying$)
            )
            .subscribe(async (result: EventMessage) => {
                // Checking for the forgot password error. Learn more about B2C error codes at
                // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
                if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
                    const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
                        authority: b2cPolicies.authorities.resetPassword.authority,
                        scopes: [],
                    };

                    await this.login(resetPasswordFlowRequest);
                }
            });
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    async checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        const activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            const accounts = this.authService.instance.getAllAccounts();
            // add your code for handling multiple accounts here
            this.authService.instance.setActiveAccount(accounts[0]);
        }

        if (this.authService.instance.getActiveAccount()) {
            var accountInfo = this.authService.instance.getActiveAccount();

            if (accountInfo !== null)
                this.updateUserInformation(accountInfo);

            var idTokenClaims = accountInfo!.idTokenClaims;

            if (idTokenClaims !== undefined && idTokenClaims.exp! > Date.now()) {
                await this.updateAccessRights(idTokenClaims);
            } else {
                let token = await this.getAccessToken() + "";
                let decodedJWT = JSON.parse(window.atob(token.split('.')[1]));
                await this.updateAccessRights(decodedJWT);
            }
        }
    }

    private async updateAccessRights(tokenClaims: any) {

        if (tokenClaims === undefined) {
            return;
        }

        const authClaim = tokenClaims.extension_AUMA_UMS_USERRIGHTSCLAIM1 ?? '' +
            tokenClaims.extension_AUMA_UMS_USERRIGHTSCLAIM2 ?? '' +
            tokenClaims.extension_AUMA_UMS_USERRIGHTSCLAIM3 ?? '' +
            tokenClaims.extension_AUMA_UMS_USERRIGHTSCLAIM4 ?? '' +
            tokenClaims.extension_AUMA_UMS_USERRIGHTSCLAIM5 ?? '';

        const accessRights = new AccessRights(authClaim);

        this.accessRightsSubject.next(accessRights);

        this.userSubject.next({
            familyName: tokenClaims.family_name,
            giveName: tokenClaims.given_name,
            mail: tokenClaims.name,
            cloudId: tokenClaims.oid
        });

        this.initialziedSubject.next(true);
    }

    private updateUserInformation(accInfo: AccountInfo) {
        this.basicUserSubject.next({
            mail: accInfo.name,
            cloudId: accInfo.homeAccountId
        });
    }

    async login(userFlowRequest?: RedirectRequest | PopupRequest) {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginPopup({...this.msalGuardConfig.authRequest, ...userFlowRequest} as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            } else {
                this.authService.loginPopup(userFlowRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                await this.authService.instance.loginRedirect({...this.msalGuardConfig.authRequest, ...userFlowRequest} as RedirectRequest);
            } else {
                await this.authService.instance.loginRedirect(userFlowRequest);
            }
        }
    }

    public logout() {
        this.authService.logout();
    }

    public onDestroy() {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

}

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string,
    tfp?: string,
};

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(msalConfig);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: loginRequest
    };
}
